import React from "react";
import { useTranslation } from "react-i18next";
import "./styles/pages/SetupPage.css";
import { getBuildInfo } from "./version";
import { apiService } from "./services/api.service";
import { telemetryService } from "./services/telemetry.service";
import { GraphUser } from "./types";
import { ISV_AAD_APP_CLIENT_ID } from "./config/api.config";
import {
  Stack,
  Text,
  Toggle,
  MessageBar,
  MessageBarType,
  ProgressIndicator,
} from "@fluentui/react";

/**
 * Generate a Microsoft 365 mailNickname from a free-text display name.
 * - Lowercase, ASCII only (diacritics removed)
 * - Non-alphanumeric characters collapsed to dashes
 * - Trimmed and capped at 64 characters
 */
const generateMailNickname = (displayName: string): string => {
  const normalized = displayName
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .substring(0, 64);
  return normalized || "security-group";
};

type NameAvailability = "idle" | "too-short" | "checking" | "available" | "taken" | "error";






interface Permission {
  name: string;
  description: string;
  id: string;
  granted: boolean;
  required: boolean;
  status: string;
  type: 'Role' | 'Scope';
}

interface PermissionsStatus {
  permissions: Permission[];
  summary: {
    totalRequired: number;
    requiredGranted: number;
    requiredMissing: number;
    extraPermissions: number;
    totalPermissions: number;
    allRequiredGranted: boolean;
  };
  tokenExpiresAt: string;
}

interface SetupPageProps {
  onConfigured?: () => void;
  onNavigateToAdmin?: () => void;
  onValidConfigurationChange?: (isValid: boolean) => void;
  theme?: string;
}

// ✨ MULTI-TENANT: Tenant onboarding status
interface TenantOnboardingStatus {
  checking: boolean;
  registering: boolean;
  exists: boolean;
  tenantId?: string;
  error?: string;
}

export default function SetupPage({ onConfigured, onNavigateToAdmin, onValidConfigurationChange, theme = "default" }: SetupPageProps) {
  const { t } = useTranslation(['common', 'setupPage']);

  const [loading, setLoading] = React.useState(true);

  const setupStartTime = React.useRef(Date.now());

  // ✨ MULTI-TENANT: Tenant onboarding state
  const [tenantStatus, setTenantStatus] = React.useState<TenantOnboardingStatus>({
    checking: true,
    registering: false,
    exists: false,
  });

  // 🔐 Service Principal detection state
  const [servicePrincipalMissing, setServicePrincipalMissing] = React.useState<boolean>(false);

  // 🔒 Allowed Security Group state
  const [allowedSecurityGroup, setAllowedSecurityGroup] = React.useState<GraphUser | null>(null);
  const [showSecurityGroupPicker, setShowSecurityGroupPicker] = React.useState(false);

  // 🔒 New security group creation state
  const [newGroupName, setNewGroupName] = React.useState("");
  const [newGroupDescription, setNewGroupDescription] = React.useState("");
  const [nameAvailability, setNameAvailability] = React.useState<NameAvailability>("idle");
  const [creatingGroup, setCreatingGroup] = React.useState(false);
  const [createGroupError, setCreateGroupError] = React.useState<string | null>(null);

  // 📊 Telemetry preference state
  const [telemetryEnabled, setTelemetryEnabled] = React.useState<boolean>(true);
  const [loadingTelemetryPref, setLoadingTelemetryPref] = React.useState(false);







  const [permissionsStatus, setPermissionsStatus] = React.useState<PermissionsStatus | null>(null);
  const [loadingPermissions, setLoadingPermissions] = React.useState(false);
  const [showSuccessModal, setShowSuccessModal] = React.useState(false);

  // Progress tracking
  const [progressLogs, setProgressLogs] = React.useState<string[]>([]);
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [progressPercent, setProgressPercent] = React.useState(0);
  const [progressMessage, setProgressMessage] = React.useState("");

  // Settings initialization state
  const [settingsInitialized, setSettingsInitialized] = React.useState(false);
  const [checkingSettings, setCheckingSettings] = React.useState(false);


  // 🔐 Monitor configuration validity and notify parent component
  React.useEffect(() => {
    const hasValidConfiguration = !servicePrincipalMissing && permissionsStatus?.summary.allRequiredGranted === true;

    console.log("[SetupPage] 🔐 Configuration status changed:", {
      servicePrincipalMissing,
      allRequiredGranted: permissionsStatus?.summary.allRequiredGranted,
      hasValidConfiguration
    });

    if (onValidConfigurationChange) {
      onValidConfigurationChange(hasValidConfiguration);
    }
  }, [servicePrincipalMissing, permissionsStatus, onValidConfigurationChange]);


  // ✨ MULTI-TENANT: Check and register tenant on mount (FIRST)
  React.useEffect(() => {
    checkAndRegisterTenant();
  }, []);

  // Load SharePoint status and permissions AFTER tenant is registered
  React.useEffect(() => {
    const loadData = async () => {
      if (!tenantStatus.checking && !tenantStatus.registering) {
        setLoading(true);
        await loadPermissionsStatus();
        setLoading(false);
      }
    };
    loadData();
  }, [tenantStatus.checking, tenantStatus.registering]);



  /**
   * ✨ MULTI-TENANT: Check if tenant exists and register if needed
   * This runs FIRST before any SharePoint setup
   * 🔒 RACE CONDITION FIX: Handle "Tenant already exists" error gracefully
   */
  const checkAndRegisterTenant = async () => {
    try {
      setTenantStatus({ checking: true, registering: false, exists: false });
      addProgressLog("🔍 Vérification du tenant...");

      // 1. Get Teams context to extract Azure Tenant ID
      let teamsJs: typeof import("@microsoft/teams-js") | null = null;
      let azureTenantId: string | undefined;
      let currentUser: { id?: string; email?: string } | undefined;

      try {
        teamsJs = await import("@microsoft/teams-js");
        await teamsJs.app.initialize();

        const context = await teamsJs.app.getContext();
        azureTenantId = context.user?.tenant?.id;
        currentUser = {
          id: context.user?.id,
          email: context.user?.userPrincipalName,
        };

        addProgressLog(`✅ Azure Tenant ID récupéré: ${azureTenantId}`);
      } catch (error) {
        console.error("Error getting Teams context:", error);
        addProgressLog("⚠️ Impossible de récupérer le contexte Teams");
        setTenantStatus({
          checking: false,
          registering: false,
          exists: false,
          error: "Impossible de récupérer le contexte Teams",
        });
        return;
      }

      if (!azureTenantId) {
        addProgressLog("⚠️ Azure Tenant ID introuvable dans le contexte Teams");
        setTenantStatus({
          checking: false,
          registering: false,
          exists: false,
          error: "Azure Tenant ID introuvable",
        });
        return;
      }

      // 2. Check if tenant exists
      addProgressLog("🔍 Vérification de l'existence du tenant...");
      const checkStartTime = Date.now();
      const checkData = await apiService.checkTenant(azureTenantId);

      if (checkData.exists && checkData.tenant) {
        // Tenant exists
        addProgressLog(`✅ Tenant existant trouvé: ${checkData.tenant.tenantName}`);
        telemetryService.capture('setup_tenant_check_completed', currentUser?.id || 'unknown', {
          properties: {
            tenant_exists: true,
            tenant_id: checkData.tenant.tenantId,
            azure_tenant_id: azureTenantId,
            duration_ms: Date.now() - checkStartTime,
          }
        });
        setTenantStatus({
          checking: false,
          registering: false,
          exists: true,
          tenantId: checkData.tenant.tenantId,
        });
        return;
      }

      // 3. Tenant doesn't exist - register automatically
      addProgressLog("🚀 Enregistrement automatique du tenant...");
      setTenantStatus({ checking: false, registering: true, exists: false });

      // ✅ CROSS-TENANT: Use apiService to include user token for OBO flow
      const registerData = await apiService.registerTenant({
        azureTenantId,
        createdBy: currentUser?.id,
        createdByEmail: currentUser?.email,
      });

      if (registerData.success) {
        // ✅ FIX: Access tenant object from the correct response structure
        // The backend returns tenant directly in the response, not nested under data
        const response = registerData as any;
        const tenant = response.tenant || response.data;
        addProgressLog(`✅ Tenant enregistré avec succès: ${tenant?.tenantName || 'Inconnu'}`);
        addProgressLog(`🏷️ Subdomain: ${tenant?.subdomain || 'N/A'}`);
        telemetryService.capture('setup_tenant_registered', currentUser?.id || 'unknown', {
          properties: {
            azure_tenant_id: azureTenantId,
            tenant_name: tenant?.tenantName || '',
            subdomain: tenant?.subdomain || '',
            tenant_id: tenant?.tenantId || tenant?.azureTenantId || '',
          }
        });
        setTenantStatus({
          checking: false,
          registering: false,
          exists: true,
          tenantId: tenant?.tenantId || tenant?.azureTenantId,
        });
      } else {
        // 🔒 RACE CONDITION FIX: Handle "Tenant already exists" error
        // This can happen if another user/tab registered the tenant between check and register
        const errorMessage = registerData.error || t('common:setup.tenantRegistrationError', 'Error registering tenant');
        
        const isRaceCondition = errorMessage.includes("already exists") || errorMessage.includes("Tenant already exists");
        telemetryService.capture('setup_tenant_registration_failed', currentUser?.id || 'unknown', {
          properties: {
            error_message: errorMessage,
            azure_tenant_id: azureTenantId,
            race_condition: isRaceCondition,
          }
        });
        
        if (isRaceCondition) {
          console.warn("[SetupPage] 🔄 Race condition detected: Tenant was registered by another request. Retrying check...");
          addProgressLog("🔄 " + t('common:setup.raceConditionDetected', 'Race condition detected: Tenant was registered by another request. Checking...'));
          
          // Retry the check to get the newly registered tenant info
          try {
            const retryCheckData = await apiService.checkTenant(azureTenantId);
            
            if (retryCheckData.exists && retryCheckData.tenant) {
              addProgressLog(`✅ ${t('common:setup.tenantFoundAfterConcurrentRegistration', 'Tenant found after concurrent registration')}: ${retryCheckData.tenant.tenantName}`);
              setTenantStatus({
                checking: false,
                registering: false,
                exists: true,
                tenantId: retryCheckData.tenant.tenantId,
              });
              return;
            }
          } catch (retryError) {
            console.error("[SetupPage] ❌ Failed to retry tenant check:", retryError);
          }
        }
        
        addProgressLog(`❌ ${t('common:setup.tenantRegistrationError', 'Error registering tenant')}: ${errorMessage}`);
        setTenantStatus({
          checking: false,
          registering: false,
          exists: false,
          error: errorMessage,
        });
      }
    } catch (error) {
      console.error("Error in checkAndRegisterTenant:", error);
      addProgressLog(`❌ ${t('common:setup.unknownError', 'Unknown error')}: ${error instanceof Error ? error.message : t('common:setup.unknownError', 'Unknown error')}`);
      setTenantStatus({
        checking: false,
        registering: false,
        exists: false,
        error: error instanceof Error ? error.message : t('common:setup.unknownError', 'Unknown error'),
      });
    }
  };



  const checkSettingsInitialization = async () => {
    try {
      setCheckingSettings(true);
      const data = await apiService.getSettings();

      // Settings are initialized if:
      // 1. Request succeeds
      // 2. Data is not null (settings exist on current site)
      // 3. At least one approver exists
      if (data.success && data.data && data.data.approvers && data.data.approvers.length > 0) {
        setSettingsInitialized(true);
      } else {
        // If request fails, returns null, or no approvers, settings are not initialized
        setSettingsInitialized(false);
      }
    } catch (error) {
      console.error("Error checking settings initialization:", error);
      setSettingsInitialized(false);
    } finally {
      setCheckingSettings(false);
    }
  };

  const loadPermissionsStatus = async (): Promise<PermissionsStatus | null> => {
    try {
      setLoadingPermissions(true);
      const data = await apiService.getPermissionsStatus();

      if (data.success) {
        setPermissionsStatus(data.data);

        telemetryService.capture('setup_permissions_loaded', 'setup-page', {
          properties: {
            total_required: data.data.summary.totalRequired,
            required_granted: data.data.summary.requiredGranted,
            required_missing: data.data.summary.requiredMissing,
            extra_permissions: data.data.summary.extraPermissions,
            all_required_granted: data.data.summary.allRequiredGranted,
          }
        });

        // ✨ Si toutes les permissions requises sont accordées, mettre à jour les infos du tenant
        if (data.data.summary.allRequiredGranted) {
          console.log("[SetupPage] ✅ All required permissions granted - refreshing tenant info...");

          try {
            // Obtenir le tenant ID depuis Teams context
            const teamsJs = await import("@microsoft/teams-js");
            try { await teamsJs.app.initialize(); } catch {}
            const context = await teamsJs.app.getContext();
            const clientTenantId = context.user?.tenant?.id;

            if (!clientTenantId) {
              console.error("[SetupPage] ❌ Cannot refresh tenant info: No client tenant ID in Teams context");
              return data.data;
            }

            // Récupérer l'état actuel du tenant pour comparaison
            const currentTenantResponse = await apiService.getTenantInfo(clientTenantId);
            const currentTenant = currentTenantResponse.success ? currentTenantResponse.data : null;

            console.log("[SetupPage] 🔄 Calling refreshTenantInfo for tenant:", clientTenantId);

            const refreshResult = await apiService.refreshTenantInfo(clientTenantId);

            console.log("[SetupPage] 📊 Refresh result:", refreshResult);

            if (refreshResult.success) {
              const newTenant = refreshResult.data;

              // Comparer les anciennes et nouvelles valeurs
              const tenantNameChanged = currentTenant?.tenantName !== newTenant?.tenantName;
              const subdomainChanged = currentTenant?.subdomain !== newTenant?.subdomain;

              if (tenantNameChanged || subdomainChanged) {
                console.log("[SetupPage] ✅ Tenant info updated successfully:", {
                  tenantName: newTenant?.tenantName,
                  subdomain: newTenant?.subdomain,
                });
              } else {
                console.log("[SetupPage] 🧘 No changes detected in tenant info. No alert shown.");
              }
            } else {
              console.error("[SetupPage] ❌ Failed to refresh tenant info:", {
                error: refreshResult.error,
                message: refreshResult.message,
                details: refreshResult.data?.details
              });

              // Afficher l'erreur à l'utilisateur
              alert(t('setupPage:alerts.tenantUpdateError', {
                error: refreshResult.message || refreshResult.error
              }));
            }
          } catch (refreshError: any) {
            console.error("[SetupPage] ❌ Exception during tenant info refresh:", {
              error: refreshError.message,
              stack: refreshError.stack
            });

         alert(t('setupPage:alerts.tenantUpdateException', {
              details: refreshError.message
            }));
          }
        }
        return data.data;
      }
      return null;
    } catch (error) {
      console.error("[SetupPage] Error loading permissions status:", error);
      return null;
    } finally {
      setLoadingPermissions(false);
    }
  };

  const refreshPermissions = async () => {
    try {
      console.log("[SetupPage] Refreshing Graph token and permissions...");
      setLoadingPermissions(true);

      telemetryService.capture('setup_permissions_refresh_clicked', 'setup-page', { properties: {} });

      // Force token refresh by resetting Graph client
      const refreshData = await apiService.refreshToken();

      console.log("[SetupPage] Token refresh response:", refreshData);

      // Reload permissions status with the new token
      const latestPermissions = await loadPermissionsStatus();

      // If all permissions are granted AND security group is configured → show success modal
      if (latestPermissions?.summary.allRequiredGranted && allowedSecurityGroup) {
        console.log("[SetupPage] ✅ All permissions granted and security group configured - showing success modal");
        telemetryService.capture('setup_completed_after_permissions', 'setup-page', {
          properties: {
            duration_ms: Date.now() - setupStartTime.current,
            permissions_all_granted: true,
            security_group_configured: true,
          }
        });
        setShowSuccessModal(true);
        return;
      }

      console.log("[SetupPage] Permissions refreshed successfully");
    } catch (error) {
      console.error("[SetupPage] Error refreshing permissions:", error);
      setLoadingPermissions(false);
    }
  };

  // 🔒 Load the allowed security group
  const loadAllowedSecurityGroup = async () => {
    try {
      const resp = await apiService.getAllowedSecurityGroup();
      console.log("loadAllowedSecurityGroup response:", resp);
      if (resp.success && resp.data) {
        console.log("Setting allowedSecurityGroup to:", resp.data);
        setAllowedSecurityGroup(resp.data);
      } else {
        setAllowedSecurityGroup(null);
      }
    } catch (e) {
      console.error("Error loading allowed security group", e);
    }
  };

  // 🔒 Remove allowed security group
  const handleRemoveAllowedSecurityGroup = async () => {
    const groupId = allowedSecurityGroup?.id;
    setLoadingPermissions(true);
    try {
      const resp = await apiService.removeAllowedSecurityGroup();
      if (resp.success) {
        setAllowedSecurityGroup(null);
        if (groupId) {
          telemetryService.capture('setup_security_group_removed', 'setup-page', {
            properties: { group_id: groupId }
          });
        }
      } else {
        console.error("Error removing allowed security group:", resp.error);
      }
    } catch (err) {
      console.error("Error removing allowed security group:", err);
    } finally {
      setLoadingPermissions(false);
    }
  };

  // 🔒 Debounced availability check for the new group name
  React.useEffect(() => {
    const trimmed = newGroupName.trim();
    if (trimmed.length === 0) {
      setNameAvailability("idle");
      return;
    }
    if (trimmed.length < 2) {
      setNameAvailability("too-short");
      return;
    }
    setNameAvailability("checking");
    let cancelled = false;
    const handle = window.setTimeout(async () => {
      const result = await apiService.checkSecurityGroupNameAvailable(trimmed);
      if (cancelled) return;
      if (result.error) {
        setNameAvailability("error");
      } else if (result.available) {
        setNameAvailability("available");
      } else {
        setNameAvailability("taken");
      }
    }, 500);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, [newGroupName]);

  // 🔒 Reset modal state when it closes
  React.useEffect(() => {
    if (!showSecurityGroupPicker) {
      setNewGroupName("");
      setNewGroupDescription("");
      setNameAvailability("idle");
      setCreateGroupError(null);
      setCreatingGroup(false);
    }
  }, [showSecurityGroupPicker]);

  // 🔒 Create the security group then lock it as the allowed group
  const handleCreateAndConfigureSecurityGroup = async () => {
    const displayName = newGroupName.trim();
    if (!displayName || nameAvailability !== "available") return;

    setCreatingGroup(true);
    setCreateGroupError(null);

    try {
      // Get current user's AAD object id to set as initial owner
      let ownerId: string | undefined;
      try {
        const teamsJs = await import("@microsoft/teams-js");
        try { await teamsJs.app.initialize(); } catch {}
        const context = await teamsJs.app.getContext();
        ownerId = context.user?.id;
      } catch (e) {
        console.warn("[SetupPage] Could not retrieve current user id from Teams context", e);
      }

      const payload = {
        displayName,
        mailNickname: generateMailNickname(displayName),
        description: newGroupDescription.trim() || undefined,
        owners: ownerId ? [ownerId] : [],
        members: ownerId ? [ownerId] : [],
      };

      const createResp = await apiService.createSecurityGroup(payload);
      if (!createResp.success || !createResp.data) {
        const message = (createResp as any).error || (createResp as any).message || t('setupPage:securityGroupModal.createErrorGeneric', 'Failed to create the security group');
        setCreateGroupError(message);
        setCreatingGroup(false);
        return;
      }

      // Now configure the newly created group as the allowed security group
      const created = createResp.data;
      const groupForLock: GraphUser = {
        id: created.id,
        displayName: created.displayName,
        mail: created.mail || "",
        userPrincipalName: created.userPrincipalName || created.mail || "",
      };

      const lockResp = await apiService.setAllowedSecurityGroup(groupForLock);
      if (lockResp.success && lockResp.data) {
        setAllowedSecurityGroup(lockResp.data);
        setShowSecurityGroupPicker(false);

        telemetryService.capture('setup_security_group_created', ownerId || 'unknown', {
          properties: {
            group_id: created.id,
            group_name: created.displayName,
            is_owner_added: !!ownerId,
          }
        });

        if (permissionsStatus?.summary.allRequiredGranted) {
          telemetryService.capture('setup_completed_after_group_creation', ownerId || 'unknown', {
            properties: {
              duration_ms: Date.now() - setupStartTime.current,
              permissions_all_granted: true,
              security_group_configured: true,
            }
          });
          setShowSuccessModal(true);
        }
      } else {
        setCreateGroupError(lockResp.error || t('setupPage:securityGroupModal.lockErrorGeneric', 'Group created but could not be configured as the allowed group'));
      }
    } catch (e: any) {
      console.error("[SetupPage] Error creating security group:", e);
      setCreateGroupError(e?.message || t('setupPage:securityGroupModal.createErrorGeneric', 'Failed to create the security group'));
    } finally {
      setCreatingGroup(false);
    }
  };

  // Load security group on mount
  React.useEffect(() => {
    if (!tenantStatus.checking && !tenantStatus.registering) {
      loadAllowedSecurityGroup();
    }
  }, [tenantStatus.checking, tenantStatus.registering]);

  // Load telemetry preference on mount
  const loadTelemetryPreference = async () => {
    try {
      setLoadingTelemetryPref(true);
      const data = await apiService.getTelemetryPreferences();

      if (data.success && data.data !== undefined) {
        setTelemetryEnabled(data.data.telemetryEnabled ?? true); // Default to true if undefined
      } else {
        // Default to true if no preference is set
        setTelemetryEnabled(true);
      }
    } catch (error) {
      console.error("Error loading telemetry preference:", error);
      // Default to true on error
      setTelemetryEnabled(true);
    } finally {
      setLoadingTelemetryPref(false);
    }
  };

  React.useEffect(() => {
    if (!tenantStatus.checking && !tenantStatus.registering) {
      loadTelemetryPreference();
    }
  }, [tenantStatus.checking, tenantStatus.registering]);


  const addProgressLog = (message: string) => {
    setProgressLogs(prev => [...prev, `${new Date().toLocaleTimeString('fr-FR')} - ${message}`]);
  };





  if (loading || tenantStatus.checking) {
    return (
      <div className={`setup-page theme-${theme}`}>
        <div className="setup-loading">
          <div className="spinner"></div>
          <p>
            {tenantStatus.checking
              ? t('setupPage:loading.tenantChecking')
              : t('setupPage:loading.configurationChecking')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`setup-page theme-${theme}`}>
      <div className="setup-header">
        <h1>{t('setupPage:header.title')}</h1>
        <p>{t('setupPage:header.subtitle')}</p>
      </div>

      {/* ✨ MULTI-TENANT: Tenant Onboarding Status */}
      {(tenantStatus.checking || tenantStatus.registering || tenantStatus.error) && (
        <div className="setup-section">
          <h2>{t('setupPage:tenantOnboarding.title')}</h2>
          <div className="status-card">
            {tenantStatus.checking && (
              <MessageBar messageBarType={MessageBarType.info}>
                {t('setupPage:tenantOnboarding.checking')}
              </MessageBar>
            )}
            {tenantStatus.registering && (
              <MessageBar messageBarType={MessageBarType.info}>
                {t('setupPage:tenantOnboarding.registering')}
              </MessageBar>
            )}
            {tenantStatus.error && (
              <MessageBar messageBarType={MessageBarType.error}>
                {t('setupPage:tenantOnboarding.error', { error: tenantStatus.error })}
              </MessageBar>
            )}
            {tenantStatus.exists && tenantStatus.tenantId && (
              <MessageBar messageBarType={MessageBarType.success}>
                {t('setupPage:tenantOnboarding.success', { tenantId: tenantStatus.tenantId })}
              </MessageBar>
            )}
          </div>
        </div>
      )}

      {/* Progress Section - Sticky at top */}
      {isProcessing && (
        <div className="progress-section">
          <MessageBar
            messageBarType={MessageBarType.info}
            onDismiss={() => setIsProcessing(false)}
            dismissButtonAriaLabel={t('setupPage:alerts.close')}
          >
            {progressMessage}
          </MessageBar>
          <ProgressIndicator
            label={t('setupPage:progress.label')}
            description={progressMessage}
            percentComplete={progressPercent}
          />
          {progressLogs.length > 0 && (
            <div className="progress-logs">
              <Stack tokens={{ childrenGap: 4 }}>
                {progressLogs.map((log, index) => (
                  <Text key={index} variant="small" block styles={{ root: { fontFamily: 'monospace', padding: '4px 8px', background: '#f3f2f1', borderRadius: '4px' } }}>
                    {log}
                  </Text>
                ))}
              </Stack>
            </div>
          )}
        </div>
      )}

      {/* Status Section */}
      <div className="setup-section">
        {servicePrincipalMissing ? (
          <>
            <div style={{
              padding: "40px",
              background: "#ffffff",
              border: "3px solid #6264a7",
              borderRadius: "16px",
              marginTop: "30px",
              textAlign: "center",
              boxShadow: "0 4px 12px rgba(0,0,0,0.15)"
            }}>
              <div style={{ fontSize: "64px", marginBottom: "20px" }}>🔐</div>
              <h2 style={{ color: "#323130", fontSize: "28px", marginTop: 0, marginBottom: "20px", fontWeight: "600" }}>
                {t('setupPage:servicePrincipal.title')}
              </h2>
              <p style={{ fontSize: "18px", marginBottom: "30px", lineHeight: "1.6", color: "#323130" }}>
                {t('setupPage:servicePrincipal.description')}
              </p>

              <button
                className="btn btn-primary"
                onClick={async () => {
                  if (!ISV_AAD_APP_CLIENT_ID) {
                    alert(t('setupPage:servicePrincipal.configMissing'));
                    return;
                  }

                  let clientTenantId: string | undefined;
                  try {
                    const teamsJs = await import("@microsoft/teams-js");
                    try { await teamsJs.app.initialize(); } catch {}
                    const context = await teamsJs.app.getContext();
                    clientTenantId = context.user?.tenant?.id;
                  } catch (error) {
                    console.error('Could not get client tenant ID:', error);
                  }

                  if (!clientTenantId) {
                    alert(t('setupPage:servicePrincipal.tenantIdError'));
                    return;
                  }

                  const consentUrl = `https://login.microsoftonline.com/${clientTenantId}/adminconsent?client_id=${ISV_AAD_APP_CLIENT_ID}`;
                  console.log('[AdminConsent] Opening:', consentUrl);
                  telemetryService.capture('setup_admin_consent_initiated', 'setup-page', {
                    properties: {
                      client_tenant_id: clientTenantId,
                      client_id: ISV_AAD_APP_CLIENT_ID,
                    }
                  });
                  window.open(consentUrl, '_blank');
                }}
                style={{ fontSize: "20px", padding: "16px 40px", marginBottom: "25px", fontWeight: "600" }}
              >
                {t('setupPage:servicePrincipal.createButton')}
              </button>

              <div style={{
                background: "#f3f2f1",
                padding: "24px",
                borderRadius: "8px",
                marginTop: "30px",
                textAlign: "left",
                border: "1px solid #e1dfdd"
              }}>
                <h3 style={{ marginTop: 0, color: "#6264a7", fontSize: "18px", fontWeight: "600" }}>{t('setupPage:servicePrincipal.instructions.title')}</h3>
                <ol style={{ marginLeft: "20px", lineHeight: "2", color: "#323130", fontSize: "15px" }}>
                  <li>{t('setupPage:servicePrincipal.instructions.step1')}</li>
                  <li>{t('setupPage:servicePrincipal.instructions.step2')}</li>
                  <li>{t('setupPage:servicePrincipal.instructions.step3')}</li>
                  <li>{t('setupPage:servicePrincipal.instructions.step4')}</li>
                </ol>
              </div>

              <button
                className="btn btn-secondary"
                onClick={() => window.location.reload()}
                style={{ marginTop: "25px", fontSize: "16px", fontWeight: "500" }}
              >
                {t('setupPage:servicePrincipal.refreshButton')}
              </button>
            </div>
          </>
        ) : (
          <>
            <h2>{t('setupPage:configStatus.title')}</h2>

            <div className="status-grid">
              {/* Permissions Microsoft Graph */}
              <div className="status-card">
                <div className="status-header">
                  <span className="status-icon">
                    {servicePrincipalMissing ? "🚫" : loadingPermissions ? "⏳" : permissionsStatus?.summary.allRequiredGranted ? "✅" : "⚠️"}
                  </span>
                  <h3>{t('setupPage:permissions.title')}</h3>
                </div>
                <div className="status-content">
                  {loadingPermissions ? (
                    <div className="loading-spinner">
                      <div className="spinner-small"></div>
                      <span>{t('setupPage:permissions.checking')}</span>
                    </div>
                  ) : permissionsStatus ? (
                    <>
                      <div className="tables-list">
                        {permissionsStatus.permissions.filter(p => p.required).map((permission, index) => (
                          <div key={permission.id || index} className={`table-item ${permission.granted ? "exists" : "missing"}`}>
                            <span className="table-status">{permission.granted ? "✅" : "❌"}</span>
                            <div style={{ display: "flex", flexDirection: "column", gap: "4px", flex: 1 }}>
                              <span className="table-name">{permission.name}</span>
                              <span style={{ fontSize: "11px", color: "#666", fontStyle: "italic" }}>
                                {permission.type === "Role" ? t('setupPage:permissions.roleType') : t('setupPage:permissions.scopeType')}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>

                      {permissionsStatus.summary.allRequiredGranted ? (
                        <p className="status-ok">
                          {t('setupPage:permissions.allGranted')}
                          {permissionsStatus.summary.extraPermissions > 0 && (
                            <> {t('setupPage:permissions.extraCount', { count: permissionsStatus.summary.extraPermissions })}</>
                          )}
                        </p>
                      ) : (
                        <>
                          <p className="status-warning">
                            {t('setupPage:permissions.missingCount', { count: permissionsStatus.summary.requiredMissing })}
                          </p>
                          <button
                              className="btn btn-primary btn-small"
                              onClick={async () => {
                                if (!ISV_AAD_APP_CLIENT_ID) {
                                  console.error('Missing ISV_AAD_APP_CLIENT_ID in environment variables');
                                  alert(t('setupPage:permissions.configMissing'));
                                  return;
                                }

                                // Obtenir le tenant ID CLIENT depuis Teams context
                                let clientTenantId: string | undefined;
                                try {
                                  const teamsJs = await import("@microsoft/teams-js");
                                  try { await teamsJs.app.initialize(); } catch {}
                                  const context = await teamsJs.app.getContext();
                                  clientTenantId = context.user?.tenant?.id;
                                } catch (error) {
                                  console.error('Could not get client tenant ID from Teams context:', error);
                                }

                                if (!clientTenantId) {
                                  alert(t('setupPage:permissions.tenantIdError'));
                                  return;
                                }

                                // Consentement dans le tenant CLIENT (pas le tenant ISV)
                                const consentUrl = `https://login.microsoftonline.com/${clientTenantId}/adminconsent?client_id=${ISV_AAD_APP_CLIENT_ID}`;
                                console.log('[AdminConsent] Opening consent URL for CLIENT tenant:', { clientTenantId, consentUrl });
                                window.open(consentUrl, '_blank');
                              }}
                              style={{ marginTop: "10px", marginRight: "10px" }}
                            >
                              {t('setupPage:permissions.grantButton')}
                            </button>
                        </>
                      )}

                      <button
                        className="btn btn-secondary btn-small"
                        onClick={refreshPermissions}
                        style={{ marginTop: "10px" }}
                      >
                        {t('setupPage:permissions.refreshButton')}
                      </button>

                      {permissionsStatus.permissions.filter(p => !p.required).length > 0 && (
                        <details style={{ marginTop: "10px" }}>
                          <summary style={{ cursor: 'pointer', color: '#6264a7', fontSize: '14px' }}>
                            {t('setupPage:permissions.showExtra')}
                          </summary>
                          <div className="tables-list" style={{ marginTop: "8px" }}>
                            {permissionsStatus.permissions.filter(p => !p.required).map((permission, index) => (
                              <div key={permission.id || index} className="table-item exists">
                                <span className="table-status">✅</span>
                                <span className="table-name">{permission.name}</span>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </>
                  ) : (
                    <p className="status-warning">{t('setupPage:permissions.loadError')}</p>
                  )}
                </div>
              </div>
            </div>


            {/* 🔒 Restricted Team Creation - Security Group Lock Section */}
            <div className="setup-section">
              <h2>{t('setupPage:securityGroup.title')}</h2>
              <p className="help-text">
                {t('setupPage:securityGroup.description')}
              </p>

              <div className="warning-card" style={{ background: "#fff7ce", border: "1px solid #f0ad4e", padding: "12px", borderRadius: "4px", marginBottom: "16px" }}>
                <div style={{ color: "#8a6d3b", fontSize: "14px" }}>
                  <strong>{t('setupPage:securityGroup.warning.title')}</strong>
                  <ul style={{ margin: "8px 0 0 0", paddingLeft: "20px" }}>
                    <li>{t('setupPage:securityGroup.warning.item1')}</li>
                    <li>{t('setupPage:securityGroup.warning.item2')}</li>
                    <li>{t('setupPage:securityGroup.warning.item3')}</li>
                    <li>{t('setupPage:securityGroup.warning.item4')}</li>
                    <li>{t('setupPage:securityGroup.warning.item5')}</li>
                    <li>{t('setupPage:securityGroup.warning.item6')}</li>
                    <li>{t('setupPage:securityGroup.warning.item7')}</li>
                  </ul>
                  {t('setupPage:securityGroup.warning.note')}
                </div>
              </div>

              <div className="status-card">
                <div className="status-content">
                  <div className="admin-status-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                    <h3 className="admin-section-title">{t('setupPage:securityGroup.label')}</h3>
                    {!allowedSecurityGroup && (
                      <button
                        onClick={() => setShowSecurityGroupPicker(true)}
                        disabled={loadingPermissions || !permissionsStatus?.summary.allRequiredGranted}
                        className="admin-add-btn"
                        style={{ padding: "8px 16px", fontSize: "14px" }}
                      >
                        {t('setupPage:securityGroup.configureButton')}
                      </button>
                    )}
                  </div>

                  {allowedSecurityGroup ? (
                    <div className="admin-security-group-display">
                      <div className="admin-security-group-flex">
                        <div className="admin-security-group-info">
                          <div className="admin-security-group-name">
                            {allowedSecurityGroup.displayName}
                          </div>
                          <div className="admin-security-group-email">
                            {allowedSecurityGroup.mail || allowedSecurityGroup.userPrincipalName}
                          </div>
                        </div>
                        <button
                          onClick={handleRemoveAllowedSecurityGroup}
                          disabled={loadingPermissions}
                          className="admin-delete-btn"
                        >
                          {t('setupPage:securityGroup.removeButton')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ background: !permissionsStatus?.summary.allRequiredGranted ? "#fff3cd" : "#fdeaf1", padding: "12px", borderRadius: "4px", color: !permissionsStatus?.summary.allRequiredGranted ? "#856404" : "#d13438", fontSize: "14px" }}>
                      {!permissionsStatus?.summary.allRequiredGranted ? (
                        <>
                          <strong>⚠️ {t('setupPage:securityGroup.permissionsFirst')}</strong>
                          <p style={{ margin: "8px 0 0 0", fontSize: "13px" }}>
                            {t('setupPage:securityGroup.permissionsFirstDescription')}
                          </p>
                        </>
                      ) : (
                        t('setupPage:securityGroup.notConfigured')
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* 📊 Telemetry Preferences Section */}
            <div className="setup-section">
              <h2>{t('setupPage:telemetry.title')}</h2>
              <p className="help-text">
                {t('setupPage:telemetry.description')}
              </p>

              <div className="info-card" style={{ background: "#e7f3ff", border: "1px solid #6264a7", padding: "12px", borderRadius: "4px", marginBottom: "16px" }}>
                <div style={{ color: "#003768", fontSize: "14px" }}>
                  <strong>{t('setupPage:telemetry.info')}</strong>
                </div>
              </div>

              <div className="status-card">
                <div className="status-content">
                  <div className="telemetry-toggle-container">
                    <Toggle
                      checked={telemetryEnabled}
                      onChange={async (event, checked) => {
                        setLoadingTelemetryPref(true);
                        try {
                          // Call API to update telemetry preference
                          const response = await apiService.updateTelemetryPreferences(checked ?? false);

                          if (response.success) {
                            setTelemetryEnabled(checked ?? false);
                            telemetryService.capture('setup_telemetry_toggled', 'setup-page', {
                              properties: { telemetry_enabled: checked ?? false }
                            });
                            console.log(`Telemetry ${checked ? 'enabled' : 'disabled'} successfully`);
                          } else {
                            console.error("Failed to update telemetry preference:", response.error);
                            // Revert the change if API call fails
                            setTelemetryEnabled(!checked);
                            alert(t('setupPage:telemetry.updateError', { error: response.error }));
                          }
                        } catch (error) {
                          console.error("Error updating telemetry preference:", error);
                          setTelemetryEnabled(!checked);
                          alert(t('setupPage:telemetry.updateErrorGeneric'));
                        } finally {
                          setLoadingTelemetryPref(false);
                        }
                      }}
                      disabled={loadingTelemetryPref}
                      label={telemetryEnabled ? t('setupPage:telemetry.enabledLabel') : t('setupPage:telemetry.disabledLabel')}
                      onText={t('setupPage:telemetry.onText')}
                      offText={t('setupPage:telemetry.offText')}
                      styles={{
                        root: {
                          marginBottom: 'var(--spacing-sm)',
                        },
                        label: {
                          fontSize: 'var(--font-size-base)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--color-text-primary)',
                        },
                        thumb: {
                          backgroundColor: telemetryEnabled ? 'white' : 'var(--color-text-secondary)',
                        }
                      }}
                    />
                    {loadingTelemetryPref && (
                      <div className="telemetry-loading-indicator">
                        <div className="spinner-small"></div>
                        <span>{t('setupPage:telemetry.updating')}</span>
                      </div>
                    )}
                  </div>
                  <p className="telemetry-description">
                    {telemetryEnabled ? t('setupPage:telemetry.enabledDescription') : t('setupPage:telemetry.disabledDescription')}
                  </p>
                </div>
              </div>
            </div>


          </>
        )}
      </div>



      {/* Success Modal */}
      {showSuccessModal && (
        <div className="success-modal-overlay">
          <div className="notification-modal notification-modal-success">
            <div className="notification-modal-header">
              <div className="notification-modal-title-container">
                <span className="notification-modal-icon">🎉</span>
                <h2 className="notification-modal-title">
                  {t('setupPage:successModal.title')}
                </h2>
              </div>
              <button
                className="notification-modal-close"
                onClick={() => setShowSuccessModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="notification-modal-body">
              <p className="notification-modal-message">
                {t('setupPage:successModal.message')}
              </p>
            </div>
            <div className="notification-modal-footer">
              <button
                className="btn btn-primary"
                onClick={onNavigateToAdmin}
              >
                {t('setupPage:successModal.goToAdminButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Create a new security group and lock it as the allowed group */}
      {showSecurityGroupPicker && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-box">
            <div className="admin-modal-header">
              <h3 style={{ margin: "0 0 4px 0", color: "var(--color-text-primary)", fontSize: "18px", fontWeight: "600" }}>
                {t('setupPage:securityGroupModal.title')}
              </h3>
              <p style={{ margin: "0", color: "var(--color-text-secondary)", fontSize: "13px" }}>
                {t('setupPage:securityGroupModal.subtitle')}
              </p>
              <button
                onClick={() => setShowSecurityGroupPicker(false)}
                disabled={creatingGroup}
                style={{
                  position: "absolute",
                  top: "16px",
                  right: "16px",
                  background: "none",
                  border: "none",
                  fontSize: "24px",
                  cursor: creatingGroup ? "not-allowed" : "pointer",
                  color: "var(--color-text-secondary)"
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: "16px", borderTop: "1px solid #e1dfdd", display: "flex", flexDirection: "column", gap: "12px" }}>
              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px", color: "var(--color-text-primary)" }}>
                  {t('setupPage:securityGroupModal.nameLabel')}
                </label>
                <input
                  type="text"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.target.value)}
                  placeholder={t('setupPage:securityGroupModal.namePlaceholder')}
                  disabled={creatingGroup}
                  autoFocus
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    fontSize: "14px",
                    border: "1px solid var(--color-border-primary, #e1dfdd)",
                    borderRadius: "4px",
                    boxSizing: "border-box",
                  }}
                />
                {newGroupName.trim().length > 0 && (
                  <div style={{ marginTop: "6px", fontSize: "12px", minHeight: "16px" }}>
                    {nameAvailability === "too-short" && (
                      <span style={{ color: "#8a6d3b" }}>{t('setupPage:securityGroupModal.minLength')}</span>
                    )}
                    {nameAvailability === "checking" && (
                      <span style={{ color: "var(--color-text-secondary)" }}>{t('setupPage:securityGroupModal.checking')}</span>
                    )}
                    {nameAvailability === "available" && (
                      <span style={{ color: "#107c10" }}>{t('setupPage:securityGroupModal.available')}</span>
                    )}
                    {nameAvailability === "taken" && (
                      <span style={{ color: "#d13438" }}>{t('setupPage:securityGroupModal.notAvailable')}</span>
                    )}
                    {nameAvailability === "error" && (
                      <span style={{ color: "#d13438" }}>{t('setupPage:securityGroupModal.checkError')}</span>
                    )}
                  </div>
                )}
                {nameAvailability === "available" && (
                  <div style={{ marginTop: "6px", fontSize: "11px", color: "var(--color-text-secondary)" }}>
                    {t('setupPage:securityGroupModal.mailNicknamePreview', { nickname: generateMailNickname(newGroupName.trim()) })}
                  </div>
                )}
              </div>

              <div>
                <label style={{ display: "block", fontSize: "13px", fontWeight: 600, marginBottom: "6px", color: "var(--color-text-primary)" }}>
                  {t('setupPage:securityGroupModal.descriptionLabel')}
                </label>
                <textarea
                  value={newGroupDescription}
                  onChange={(e) => setNewGroupDescription(e.target.value)}
                  placeholder={t('setupPage:securityGroupModal.descriptionPlaceholder')}
                  disabled={creatingGroup}
                  rows={3}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    fontSize: "14px",
                    border: "1px solid var(--color-border-primary, #e1dfdd)",
                    borderRadius: "4px",
                    boxSizing: "border-box",
                    resize: "vertical",
                    fontFamily: "inherit",
                  }}
                />
              </div>

              {createGroupError && (
                <MessageBar messageBarType={MessageBarType.error}>
                  {createGroupError}
                </MessageBar>
              )}
              {creatingGroup && (
                <MessageBar messageBarType={MessageBarType.info}>
                  {t('setupPage:securityGroupModal.creating')}
                </MessageBar>
              )}
            </div>
            <div style={{ padding: "16px", borderTop: "1px solid var(--color-border-primary)", textAlign: "right", background: "var(--color-bg-secondary)", display: "flex", justifyContent: "flex-end", gap: "8px" }}>
              <button
                onClick={() => setShowSecurityGroupPicker(false)}
                disabled={creatingGroup}
                style={{ padding: "8px 16px", fontSize: "13px", background: "var(--color-bg-primary)", border: "1px solid var(--color-border-primary)", borderRadius: "2px", cursor: creatingGroup ? "not-allowed" : "pointer", color: "var(--color-text-primary)" }}
              >
                {t('setupPage:securityGroupModal.cancelButton')}
              </button>
              <button
                onClick={handleCreateAndConfigureSecurityGroup}
                disabled={creatingGroup || nameAvailability !== "available"}
                className="btn btn-primary"
                style={{ padding: "8px 16px", fontSize: "13px", opacity: (creatingGroup || nameAvailability !== "available") ? 0.6 : 1, cursor: (creatingGroup || nameAvailability !== "available") ? "not-allowed" : "pointer" }}
              >
                {creatingGroup
                  ? t('setupPage:securityGroupModal.creating')
                  : t('setupPage:securityGroupModal.createButton')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Refresh Button */}
      {!servicePrincipalMissing && (
        <div className="setup-footer">
          <button className="btn btn-secondary" onClick={refreshPermissions} disabled={loading}>
            {t('setupPage:footer.refreshButton')}
          </button>

          {/* Version Info */}
          <div className="version-info">
            <small>
              {t('setupPage:footer.versionInfo', {
                version: getBuildInfo().fullVersion,
                buildNumber: getBuildInfo().buildNumber,
                buildDate: new Date(getBuildInfo().buildDate).toLocaleString('fr-FR')
              })}
            </small>
          </div>
        </div>
      )}
    </div>
  );
}
